import React, { useState, useEffect, useRef } from "react";
import { useSearchQuery, usePagination } from "../../../hooks";
import { MPSharedHeader, MPSharedTable, MPSharedDeleteModal } from "../Shared/";
import {
  useGetDataAccessAssignmentsQuery,
  useDeleteDataAccessAssignmentMutation,
} from "../services";
import { getLocalizedString } from "../../../shared/translation";
import { SvgIcon } from "../../Shared";
import { DataAccessForm } from "./form";

// Permission key for gating create/edit/delete (see design Open Question 1).
const RESOURCE_NAME = "Data Access Assignments";

export const DataAccessContainer = () => {
  const { page, setPage, perPage, pagination } = usePagination();

  const { setSimpleSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  const {
    data = { data: [], total_count: 0 },
    error,
    isFetching,
    isSuccess,
    refetch,
  } = useGetDataAccessAssignmentsQuery({
    page,
    per_page: perPage,
    order_by: "data_access_assignments.updated_at",
    order_dir: "desc",
    q,
  });

  const [
    deleteDataAccessAssignment,
    {
      isSuccess: deleteSuccess,
      isLoading: deleteLoading,
      error: deleteError,
      reset: resetDeleteAssignment,
    },
  ] = useDeleteDataAccessAssignmentMutation();

  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToEdit, setIdToEdit] = useState(null);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState("");
  const [refreshTimedOut, setRefreshTimedOut] = useState(false);

  // 30s post-delete refresh guard (Req 5.6). When a confirmed delete succeeds we
  // wait for the list refetch to settle; if it doesn't complete in time we retain
  // the prior list and surface a manual refresh control.
  const refreshTimerRef = useRef(null);
  const sawRefetchRef = useRef(false);
  const [awaitingRefresh, setAwaitingRefresh] = useState(false);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  // Clear the guard timer if the container unmounts mid-wait.
  useEffect(() => clearRefreshTimer, []);

  const onEdit = (datum) => {
    setShowForm(true);
    setIdToEdit(datum.id);
  };

  const onDelete = (datum) => {
    setDeleteErrorMsg("");
    resetDeleteAssignment();
    setShowDeleteWarning(true);
    setIdToDelete(datum.id);
  };

  const closeDeleteModal = () => {
    setDeleteErrorMsg("");
    setShowDeleteWarning(false);
    resetDeleteAssignment();
  };

  // Manual refresh control shown after the 30s guard times out (Req 5.6).
  const manualRefresh = () => {
    setRefreshTimedOut(false);
    refetch();
  };

  const onCreate = () => {
    setShowForm(true);
    setIdToEdit(null);
  };

  const headers = [
    {
      label: getLocalizedString("name", "Name"),
      key: "name",
      className: "name",
    },
    {
      label: getLocalizedString("vehicles", "Vehicles"),
      className: "text-center",
      nestedValue: true,
      getNestedValue: ({ vehicle_ids, vehicles }) => {
        const count = Array.isArray(vehicles) ? vehicles.length : Array.isArray(vehicle_ids) ? vehicle_ids.length : 0;
        return <span>{count}</span>;
      },
    },
    {
      label: getLocalizedString("vehicle_groups", "Vehicle Groups"),
      className: "text-center",
      nestedValue: true,
      getNestedValue: ({ vehicle_group_ids, vehicle_groups }) => {
        const count = Array.isArray(vehicle_groups) ? vehicle_groups.length : Array.isArray(vehicle_group_ids) ? vehicle_group_ids.length : 0;
        return <span>{count}</span>;
      },
    },
    {
      label: getLocalizedString("devices", "Devices"),
      className: "text-center",
      nestedValue: true,
      getNestedValue: ({ device_ids, devices }) => {
        const count = Array.isArray(devices) ? devices.length : Array.isArray(device_ids) ? device_ids.length : 0;
        return <span>{count}</span>;
      },
    },
    {
      label: getLocalizedString("device_groups", "Device Groups"),
      className: "text-center",
      nestedValue: true,
      getNestedValue: ({ device_group_ids, device_groups }) => {
        const count = Array.isArray(device_groups) ? device_groups.length : Array.isArray(device_group_ids) ? device_group_ids.length : 0;
        return <span>{count}</span>;
      },
    },
    {
      label: getLocalizedString("events", "Events"),
      className: "text-center",
      nestedValue: true,
      getNestedValue: ({ event_ids, events }) => {
        const count = Array.isArray(events) ? events.length : Array.isArray(event_ids) ? event_ids.length : 0;
        return <span>{count}</span>;
      },
    },
    {
      label: getLocalizedString("added_on", "Added On"),
      key: "created_at",
      className: "date",
    },
    {
      label: getLocalizedString("updated_on", "Updated On"),
      key: "updated_at",
      className: "date",
      utc: true,
    },
    {
      label: getLocalizedString("updated_by", "Updated By"),
      className: "updated-by text-center",
      nestedValue: true,
      getNestedValue: ({ updated_by }) => {
        return (
          <>
            {updated_by == null ? (
              <span>NA</span>
            ) : (
              <span title={`${updated_by?.email}`}>
                {updated_by?.first_name} {updated_by?.last_name}
              </span>
            )}
          </>
        );
      },
    },
  ];

  const getDeleteRecord = (id) => {
    const index = data?.data?.findIndex((item) => item.id === id);
    return index >= 0 ? data?.data[index] : null;
  };

  const getDeleteItemName = (id) => getDeleteRecord(id)?.name || "";

  // Count of Roles linked to the assignment being deleted (Req 5.2). Prefer the
  // embedded roles collection, falling back to role_ids when only ids are present.
  const getLinkedRoleCount = (id) => {
    const record = getDeleteRecord(id);
    if (!record) return 0;
    if (Array.isArray(record.roles)) return record.roles.length;
    if (Array.isArray(record.role_ids)) return record.role_ids.length;
    return 0;
  };

  const linkedRoleCount = getLinkedRoleCount(idToDelete);

  // On confirmed deletion: close the modal, then start the 30s refresh guard while
  // RTK Query tag invalidation refetches the list (Req 5.5, 5.6).
  useEffect(() => {
    if (deleteSuccess) {
      setShowDeleteWarning(false);
      setDeleteErrorMsg("");
      sawRefetchRef.current = false;
      setAwaitingRefresh(true);
      setRefreshTimedOut(false);
      clearRefreshTimer();
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        setRefreshTimedOut(true);
        setAwaitingRefresh(false);
      }, 30000);
      resetDeleteAssignment();
    }
  }, [deleteSuccess]);

  // The refetch settled in time: clear the guard timer and any timeout message.
  // Wait until we have observed the refetch begin (isFetching true) before treating
  // a subsequent settle as completion, so we don't clear the guard prematurely.
  useEffect(() => {
    if (!awaitingRefresh) return;
    if (isFetching) {
      sawRefetchRef.current = true;
    } else if (sawRefetchRef.current) {
      clearRefreshTimer();
      setAwaitingRefresh(false);
      setRefreshTimedOut(false);
    }
  }, [isFetching, awaitingRefresh]);

  // On delete error retain the record, surface the message, and re-enable confirm (Req 5.7).
  useEffect(() => {
    if (deleteError) {
      if (deleteError.status === 400 && deleteError.data?.base) {
        setDeleteErrorMsg(`Can not delete, ${deleteError.data.base.join(",")}`);
      } else {
        setDeleteErrorMsg(
          getLocalizedString("data_access_delete_failed", "The deletion failed. Please try again.")
        );
      }
    }
  }, [deleteError]);

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          groupName="users_role"
          heading={getLocalizedString("users_and_roles", "Users And Roles")}
          resourceName={RESOURCE_NAME}
          createNewHandler={onCreate}
          handleSearchKey={(value) => setSimpleSearch(value)}
          simpleSearchPlaceholder={getLocalizedString("search_by_name", "Search by Name")}
        />

        {!isFetching && error && (
          <div className="no-data">
            <SvgIcon wrapperClass="no-data-icon" svgClass="icon-noData" name="app-icon" />
            <p>
              {getLocalizedString(
                "data_access_list_load_failed",
                "The list could not be loaded."
              )}
            </p>
            <button type="button" className="qi-btn secondary" onClick={() => refetch()}>
              {getLocalizedString("retry", "Retry")}
            </button>
          </div>
        )}

        {refreshTimedOut && (
          <div className="refresh-timeout-message">
            <p>
              {getLocalizedString(
                "data_access_refresh_failed",
                "The list could not be refreshed."
              )}
            </p>
            <button type="button" className="qi-btn secondary" onClick={manualRefresh}>
              {getLocalizedString("refresh", "Refresh")}
            </button>
          </div>
        )}

        <MPSharedTable
          resourceName={RESOURCE_NAME}
          data={data.data || []}
          error={error}
          isLoading={isFetching}
          isSuccess={isSuccess}
          headers={headers}
          onEdit={onEdit}
          onDelete={onDelete}
          pagination={{ ...pagination, count: data.total_count }}
          skipClientId
          className="data-access"
          auditResource="DataAccessAssignment"
          auditKey="name"
        />
      </article>

      {showForm && <DataAccessForm idToEdit={idToEdit} onClose={setShowForm} />}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        entityName={getLocalizedString("data_access_assignment", "data access assignment")}
        onHide={closeDeleteModal}
        itemName={getDeleteItemName(idToDelete)}
        error={deleteErrorMsg}
        disable={deleteLoading}
        additionalContent={
          <span>
            {getLocalizedString("linked_roles", "Linked roles")}: <strong>{linkedRoleCount}</strong>
          </span>
        }
        onDelete={() => {
          setDeleteErrorMsg("");
          deleteDataAccessAssignment({ id: idToDelete });
        }}
      />
    </>
  );
};
